/**
 * `/my/tracking/:projectId` — employee calendar shell (PRD §4.6, §6.2).
 */

import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BackLink } from "@/components/back-link";
import { TrackingCalendar } from "@/components/tracking/calendar";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { YearSelect } from "@/components/ui/year-select";
import { useQueryYear } from "@/lib/query-param";
import { useSession } from "@/lib/session";
import { useCalendar, useMyProjects } from "@/lib/tracking";
import { berlinToday } from "@/shared/dates";
import { projectYears } from "@/shared/years";

export function MyTrackingProjectPage() {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const { user } = useSession();
  const projects = useMyProjects();
  const project = (projects.data ?? []).find((row) => row.id === projectId);

  const today = berlinToday();
  const berlinYear = Number(today.slice(0, 4));

  const years = useMemo(
    () => (project ? projectYears(project.startDate, project.endDate, berlinYear) : []),
    [project, berlinYear],
  );
  const [year, setYear] = useQueryYear("year", years, berlinYear);
  const focusMonth = year === berlinYear ? Number(today.slice(5, 7)) : 1;
  const calendar = useCalendar({
    userId: user?.id,
    projectId,
    year,
    month: focusMonth,
  });

  if (projects.isLoading || calendar.isLoading) {
    return <Skeleton className="h-40 w-full rounded-xl" />;
  }

  if (!project || !user) {
    return (
      <div>
        <BackLink to="/my/tracking">{t("myTracking.back")}</BackLink>
        <p className="mt-6 text-sm text-muted-foreground">{t("myTracking.noAccess")}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <BackLink to="/my/tracking">{t("myTracking.projectsBack")}</BackLink>
        <div className="flex items-center gap-2">
          <Label className="text-sm font-medium">{t("adminExtra.year")}</Label>
          <YearSelect
            value={year}
            onChange={setYear}
            years={years}
            ariaLabel={t("adminExtra.year")}
          />
        </div>
      </div>

      <div className="mt-6">
        {calendar.data ? (
          <TrackingCalendar
            key={`${user.id}-${calendar.data.year}-${calendar.data.projectId}`}
            calendar={calendar.data}
            userId={user.id}
            userName={user.firstName}
            personLastName={user.lastName}
          />
        ) : (
          <p className="text-sm text-muted-foreground">{t("myTracking.noAccess")}</p>
        )}
      </div>
    </div>
  );
}
